/// <reference types="vite-plugin-pwa/client" />
// Getting people onto the new version after a deploy.
//
// The service worker serves the app from its cache, so without this the first
// visit after a deploy still shows the old build, and an installed app, which
// resumes rather than reloads, can sit on it for days. Ryan hit exactly that:
// a fix was live and his phone didn't have it.
//
// So: look for a new version at start and whenever the app comes back to the
// foreground, and switch to it by reloading while nobody is looking: when the
// page goes to the background (another tab, another app, the phone locked).
// A reload in view blanks the page a second after it appeared, which Ryan saw
// on the first visit after every deploy. Never in the middle of something
// either: a reload while someone is choosing a photo or typing would throw it
// away. A tab that is closed instead gets the new version on its next visit.
import { registerSW } from 'virtual:pwa-register'

let waiting = false
let urgent = false // set by newerBuild(): the page on screen is one the old build got wrong

function midTask(): boolean {
  if (/\/post$/.test(location.pathname)) return true
  const el = document.activeElement
  return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement
}

const apply = registerSW({
  immediate: true,
  onNeedRefresh() {
    waiting = true
    applyWhenIdle()
  },
  onRegisteredSW(_url, registration) {
    if (!registration) return
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') registration.update().catch(() => {})
    })
  },
})

function applyWhenIdle() {
  if (!waiting) return
  if (urgent || (document.visibilityState === 'hidden' && !midTask())) void apply(true) // activates the new worker and reloads
}

/**
 * Is there a newer build than the one that is running? Asked by the not-found
 * page, because a path this build doesn't know may be a route that a deploy
 * added after the worker cached the app (/privacy was, and showed "Nothing
 * here" for three seconds). When there is one, the reload onto it follows by
 * itself as soon as it is installed, mid-task or not: nobody is in the middle
 * of anything on a page that doesn't exist.
 *
 * The question is put to the server directly: its sw.js lists the files of the
 * build that is out, so one that doesn't name the script this page runs is a
 * newer build. (`registration.update()` answers the same thing, but Chrome
 * takes two seconds over the first one after a page load, which is two seconds
 * of spinner before every honest "Nothing here".)
 *
 * False when nothing newer was found, the check failed (offline), or it took
 * longer than `timeoutMs`.
 */
export async function newerBuild(timeoutMs = 4000): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false
  const check = async () => {
    // No registration yet means a first visit: this build came from the network.
    const registration = await navigator.serviceWorker.getRegistration()
    const entry = document.querySelector('script[type=module][src]')?.getAttribute('src')?.replace(/^\//, '')
    if (!registration || !entry) return false
    if (registration.installing || registration.waiting) return true
    const res = await fetch('/sw.js', { cache: 'no-store' })
    if (!res.ok || (await res.text()).includes(entry)) return false
    void registration.update().catch(() => {}) // fetches and installs it; onNeedRefresh follows
    return true
  }
  const found = await Promise.race([check().catch(() => false), new Promise<boolean>((done) => setTimeout(done, timeoutMs, false))])
  if (found) {
    urgent = true
    applyWhenIdle()
  }
  return found
}

document.addEventListener('visibilitychange', applyWhenIdle)
setInterval(applyWhenIdle, 20_000) // hidden mid-task, then the task ended
